import React, { useState } from 'react';
import { Button } from '../ui/button';
import { 
  ArrowRight, 
  ArrowLeft,
  Award,
  Users,
  TrendingUp,
  Sparkles,
  Target,
  Lightbulb
} from 'lucide-react';

/**
 * HP.1 Guided Input: Impact Visibility Wizard
 * Captures user achievements to generate an impact snapshot.
 * 3 steps: Role → Achievements → Impact & Recognition
 */

const STEPS = [
  {
    id: 'role',
    title: 'About You',
    subtitle: 'Quick context about your role',
    icon: Users
  },
  {
    id: 'achievements',
    title: 'Your Hidden Wins',
    subtitle: 'Work that deserves more recognition',
    icon: Award
  },
  {
    id: 'impact',
    title: 'The Real Impact',
    subtitle: 'Let\'s make it visible',
    icon: TrendingUp
  }
];

const VisibilityGuidedInput = ({ onComplete, onCancel, accentColor = '#8b5cf6' }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({
    role: '',
    context: '',
    achievements: ['', '', ''],
    who_benefited: '',
    obstacles: '',
    recognition_gap: '',
    optional_paste: ''
  });
  const [showOptionalPaste, setShowOptionalPaste] = useState(false);

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const updateAchievement = (index, value) => {
    setFormData(prev => {
      const newAchievements = [...prev.achievements];
      newAchievements[index] = value;
      return { ...prev, achievements: newAchievements };
    });
  };

  const canProceed = () => {
    switch (currentStep) {
      case 0:
        return formData.role.trim().length >= 2 && formData.context.trim().length >= 10;
      case 1:
        return formData.achievements.filter(a => a.trim().length > 0).length >= 1;
      case 2:
        return true; // Optional fields
      default:
        return false;
    }
  };

  const handleNext = () => {
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleSubmit();
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = () => {
    const cleanedData = {
      ...formData,
      achievements: formData.achievements.filter(a => a.trim().length > 0),
      flow_type: 'visibility'
    };
    
    sessionStorage.setItem('rv_input', JSON.stringify({
      ...cleanedData,
      created_at: new Date().toISOString()
    }));
    
    onComplete(cleanedData);
  };

  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                What's your role? *
              </label>
              <input
                type="text"
                value={formData.role}
                onChange={(e) => updateField('role', e.target.value)}
                placeholder="e.g., Backend Engineer, Marketing Manager, Operations Lead"
                className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-500/50"
                data-testid="vis-input-role"
                autoFocus
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                What do you actually do? (Not your job description) *
              </label>
              <textarea
                value={formData.context}
                onChange={(e) => updateField('context', e.target.value)}
                placeholder="e.g., I'm the person people come to when systems break. I handle the complex migrations nobody else wants to touch. I quietly mentor 3 junior devs..."
                rows={3}
                className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-500/50 resize-none"
                data-testid="vis-input-context"
              />
              <p className="text-xs text-slate-500 mt-1">
                The stuff that doesn't fit in a job title
              </p>
            </div>
          </div>
        );
      
      case 1:
        return (
          <div className="space-y-4">
            <p className="text-sm text-slate-600 mb-4">
              What have you done recently that your manager probably doesn't fully appreciate?
            </p>
            
            {formData.achievements.map((achievement, index) => (
              <div key={index} className="relative">
                <div className="absolute left-4 top-3.5 w-6 h-6 rounded-full bg-violet-100 flex items-center justify-center text-xs font-medium text-violet-600">
                  {index + 1}
                </div>
                <input
                  type="text"
                  value={achievement}
                  onChange={(e) => updateAchievement(index, e.target.value)}
                  placeholder={
                    index === 0 ? "e.g., Fixed a production bug at 2am that would have cost us the client" :
                    index === 1 ? "e.g., Documented the entire onboarding process (nobody asked, everyone benefits)" :
                    "e.g., Handled 3 escalations while the team lead was on vacation"
                  }
                  className="w-full pl-14 pr-4 py-3 bg-white border border-slate-200 rounded-xl text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-500/50"
                  data-testid={`vis-input-achievement-${index}`}
                  autoFocus={index === 0}
                />
              </div>
            ))}
            
            <div className="pt-4 border-t border-slate-100">
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Who benefited from this work? (optional)
              </label>
              <input
                type="text"
                value={formData.who_benefited}
                onChange={(e) => updateField('who_benefited', e.target.value)}
                placeholder="e.g., The sales team, new hires, the client, the whole engineering org"
                className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-500/50"
                data-testid="vis-input-benefited"
              />
            </div>
          </div>
        );
      
      case 2:
        return (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                What obstacles did you overcome? (optional)
              </label>
              <input
                type="text"
                value={formData.obstacles}
                onChange={(e) => updateField('obstacles', e.target.value)}
                placeholder="e.g., Tight deadline, unclear requirements, legacy code, team changes"
                className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-500/50"
                data-testid="vis-input-obstacles"
                autoFocus
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Why doesn't this work get the recognition it deserves? (optional)
              </label>
              <input
                type="text"
                value={formData.recognition_gap}
                onChange={(e) => updateField('recognition_gap', e.target.value)}
                placeholder="e.g., It happened behind the scenes, my manager doesn't see the details, it's not flashy"
                className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-500/50"
                data-testid="vis-input-gap"
              />
            </div>
            
            {/* Optional paste section */}
            <div className="pt-4 border-t border-slate-100">
              <button
                type="button"
                onClick={() => setShowOptionalPaste(!showOptionalPaste)}
                className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-700 transition-colors"
              >
                <Lightbulb className="w-4 h-4" />
                <span>{showOptionalPaste ? 'Hide' : 'Have Slack messages or feedback to add?'}</span>
              </button>
              
              {showOptionalPaste && (
                <div className="mt-3">
                  <textarea
                    value={formData.optional_paste}
                    onChange={(e) => updateField('optional_paste', e.target.value)}
                    placeholder="Paste any relevant messages, feedback, or notes that show the impact of your work..."
                    rows={4}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-500/50 resize-none text-sm"
                    data-testid="vis-input-optional-paste"
                  />
                </div>
              )}
            </div>
          </div>
        );
      
      default:
        return null;
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-2xl overflow-hidden max-w-xl w-full" data-testid="vis-guided-input">
      {/* Header */}
      <div className="px-6 py-4 border-b border-slate-100 bg-slate-50">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            {React.createElement(STEPS[currentStep].icon, { 
              className: "w-5 h-5",
              style: { color: accentColor }
            })}
            <div>
              <p className="font-semibold text-slate-900">{STEPS[currentStep].title}</p>
              <p className="text-sm text-slate-500">{STEPS[currentStep].subtitle}</p>
            </div>
          </div>
          {onCancel && (
            <button
              onClick={onCancel}
              className="text-slate-400 hover:text-slate-600 text-sm"
            >
              Cancel
            </button>
          )}
        </div>
        
        {/* Progress bar */}
        <div className="flex gap-2">
          {STEPS.map((step, index) => (
            <div
              key={step.id}
              className="flex-1 h-1.5 rounded-full overflow-hidden bg-slate-200"
            >
              <div
                className="h-full transition-all duration-300"
                style={{
                  width: index < currentStep ? '100%' : index === currentStep ? '50%' : '0%',
                  backgroundColor: accentColor
                }}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Form content */}
      <div className="p-6">
        {renderStep()}
      </div>

      {/* Footer */}
      <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex items-center justify-between">
        <Button
          variant="ghost"
          onClick={handleBack}
          disabled={currentStep === 0}
          className="text-slate-600"
          data-testid="vis-btn-back"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        
        <div className="flex items-center gap-3">
          <span className="text-sm text-slate-500">
            Step {currentStep + 1} of {STEPS.length}
          </span>
          
          <Button
            onClick={handleNext}
            disabled={!canProceed()}
            className="text-white"
            style={{ backgroundColor: canProceed() ? accentColor : undefined }}
            data-testid="vis-btn-next"
          >
            {currentStep === STEPS.length - 1 ? (
              <>
                <Sparkles className="w-4 h-4 mr-2" />
                Show My Impact
              </>
            ) : (
              <>
                Continue
                <ArrowRight className="w-4 h-4 ml-2" />
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default VisibilityGuidedInput;
